import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId, encodeRecordId } from '../recordIds';
import { DBLegacyTopicGroupSchema, LegacyTopicGroupSchema } from './schemas';

type DBLegacyTopicGroup = z.infer<typeof DBLegacyTopicGroupSchema>;
type LegacyTopicGroup = z.infer<typeof LegacyTopicGroupSchema>;

export const LegacyTopicGroupCodecConfig: TransformerConfig<
  typeof LegacyTopicGroupSchema,
  typeof DBLegacyTopicGroupSchema,
  'id'
> = {
  name: 'LegacyTopicGroup',
  clientSchema: LegacyTopicGroupSchema,
  dbSchema: DBLegacyTopicGroupSchema,
  transformers: [
    createRenameTransformer<typeof LegacyTopicGroupSchema, typeof DBLegacyTopicGroupSchema>(
      'code',
      'groupId'
    ),

    // topics: [{ topicId, topicCode, label }] ↔ topics: { topicCode: { key, label } }
    createTransformer<typeof LegacyTopicGroupSchema, typeof DBLegacyTopicGroupSchema>({
      encoder: {
        sourceFields: ['topics'] as const,
        encode: ({ topics }) => ({
          topics: Object.fromEntries(
            (topics as LegacyTopicGroup['topics']).map(t => [
              t.topicCode,
              { key: t.topicCode, label: t.label },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['subject', 'topics'] as const,
        decode: ({ subject, topics }) => ({
          ...(subject !== undefined && { subject }),
          // Each topic's id needs the group's subject, so a read without it gives no topics
          ...(subject !== undefined &&
            topics !== undefined && {
              // A stored entry that is not an object reads without a label, for the read's check
              // to report, rather than failing the decoder and the subject with it
              topics: Object.entries(
                topics as Record<string, NonNullable<DBLegacyTopicGroup['topics']>[string] | null>
              ).map(([topicCode, entry]) => ({
                topicId: encodeRecordId('legacyTopic', { subject, code: topicCode }),
                topicCode,
                label: entry?.label as string,
              })),
            }),
        }),
      },
    }),
  ],
  derived: { id: topicGroup => buildRecordId('legacyTopicGroup', topicGroup) },
};

export const encodeLegacyTopicGroupToDb = createEncodeToDbFn(LegacyTopicGroupCodecConfig);
export const decodeLegacyTopicGroupFromDb = createDecodeFromDbFn(LegacyTopicGroupCodecConfig);
